import type { ReactNode } from 'react'
import { useI18n } from '@/shared/i18n/useT'

export interface SelectionStats {
  rowsCount: number
  columnsCount: number
  cellsCount: number
  uniqueCount: number
  numberCount: number
  sum: number
  average: number
  max: number
  min: number
}

/** Selection totals from the selected cells: numbers feed sum/average/max/min, non-empty texts the unique count. */
export function computeSelectionStats(rowsCount: number, columnsCount: number, numbers: readonly number[], texts: Iterable<string>): SelectionStats {
  const uniques = new Set<string>()
  for (const text of texts) if (text !== '') uniques.add(text)
  const sum = numbers.reduce((a, b) => a + b, 0)
  return {
    rowsCount,
    columnsCount,
    cellsCount: rowsCount * columnsCount,
    uniqueCount: uniques.size,
    numberCount: numbers.length,
    sum,
    average: numbers.length > 0 ? sum / numbers.length : 0,
    max: numbers.length > 0 ? Math.max(...numbers) : 0,
    min: numbers.length > 0 ? Math.min(...numbers) : 0,
  }
}

export interface GridStatusBarProps {
  /** Row count shown first; omitted when undefined. */
  rowsCount?: number | undefined
  stats: SelectionStats | null
  /** Zoom percent; the zoom control is hidden without onZoomChange. */
  zoom?: number
  onZoomChange?: ((zoom: number) => void) | undefined
  className?: string
  /** Replaces the row count and selection stats while rows are selected. */
  selectionActions?: ReactNode
  /** Extra items after the row count, e.g. a text sheet's word count. */
  summary?: ReactNode
}

/** The bar under a grid or text sheet: counts, selection summary and zoom. */
export function GridStatusBar({ rowsCount, stats, zoom = 100, onZoomChange, className = '', selectionActions, summary }: GridStatusBarProps) {
  const { t } = useI18n()
  // Phones show the bar only for row actions or a multi-cell selection.
  const visibleOnPhone = Boolean(selectionActions) || (stats !== null && stats.cellsCount > 1)
  return (
    <div data-hide-while-typing className={`${visibleOnPhone ? 'flex' : 'hidden sm:flex'} h-10 sm:h-9 items-center justify-between gap-x-2 sm:gap-x-4 text-sm sm:text-xs shrink-0 min-h-0 min-w-0 ${selectionActions ? 'bg-header-bg px-2 text-header-fg' : 'text-text-muted'} ${className}`}>
      {selectionActions ? <div className="flex min-w-0 flex-1 items-center gap-x-1 whitespace-nowrap">{selectionActions}</div> : <div className="flex min-w-0 flex-1 items-center gap-x-2.5 sm:gap-x-4 overflow-x-auto whitespace-nowrap scrollbar-none">
        {rowsCount !== undefined && <span className="shrink-0">{t('grid.rowsCount', { count: rowsCount })}</span>}
        {summary}
        {stats && stats.cellsCount > 1 && <>
          <span className="shrink-0">{t('grid.selectionSummary', { rows: stats.rowsCount, columns: stats.columnsCount, cells: stats.cellsCount })}</span>
          {stats.uniqueCount > 0 && <span className="shrink-0">{t('grid.uniqueValues', { count: stats.uniqueCount })}</span>}
          {stats.numberCount > 0 && <>
            <span className="shrink-0">{t('grid.sum', { value: stats.sum })}</span>
            <span className="shrink-0">{t('grid.average', { value: stats.average })}</span>
            <span className="shrink-0">{t('grid.max', { value: stats.max })}</span>
            <span className="shrink-0">{t('grid.min', { value: stats.min })}</span>
          </>}
        </>}
      </div>}
      {onZoomChange && <div className="hidden sm:flex items-center gap-2 shrink-0">
        <div className="flex h-7 sm:h-6 overflow-hidden rounded-lg border border-border">
          <button type="button" aria-label={t('grid.zoomOut')} onClick={() => onZoomChange(Math.max(50, zoom - 10))} className="h-7 w-7 sm:h-6 sm:w-6 border-r border-border text-sm sm:text-xs flex items-center justify-center">−</button>
          <div className="flex items-center gap-0.5 px-1">
            <input
              key={zoom}
              defaultValue={zoom}
              type="text"
              inputMode="numeric"
              aria-label={t('grid.zoomInput')}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.target.select()}
              onBlur={(e) => {
                const parsed = Number(e.target.value.replace(/[^0-9-]/g, ''))
                onZoomChange(Number.isFinite(parsed) && parsed !== 0 ? Math.min(150, Math.max(50, Math.round(parsed))) : zoom)
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { e.currentTarget.value = String(zoom); e.currentTarget.blur() } }}
              className="w-7 sm:w-6 bg-transparent text-center text-sm sm:text-xs outline-none"
            />
            <span className="text-xs sm:text-[11px]">%</span>
          </div>
          <button type="button" aria-label={t('grid.zoomIn')} onClick={() => onZoomChange(Math.min(150, zoom + 10))} className="h-7 w-7 sm:h-6 sm:w-6 border-l border-border text-sm sm:text-xs flex items-center justify-center">+</button>
        </div>
      </div>}
    </div>
  )
}
